import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowUpRight, Loader2, Ticket as TicketIcon, X } from "lucide-react";
import { api, fmtBRL, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function UpgradePlanDialog({ vpsId, open, onOpenChange }) {
  const navigate = useNavigate();
  const [options, setOptions] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("pix");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let current = true;
    setLoading(true);
    setOptions(null);
    setCouponInput("");
    setCoupon(null);
    api.get(`/vps/${vpsId}/upgrade-options`)
      .then(({ data }) => {
        if (!current) return;
        setOptions(data);
        setSelectedPlanId(data.plans?.[0]?.plan_id || "");
        const methods = data.payment_methods || ["pix"];
        setPaymentMethod((currentMethod) =>
          methods.includes(currentMethod) ? currentMethod : (methods[0] || "pix")
        );
      })
      .catch((error) => {
        if (current) toast.error(formatApiError(error));
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => { current = false; };
  }, [open, vpsId]);

  const selectedPlan = options?.plans?.find((plan) => plan.plan_id === selectedPlanId);
  const discountedAmount = selectedPlan
    ? Math.round(selectedPlan.amount_brl * (1 - (coupon?.discount_pct || 0) / 100) * 100) / 100
    : 0;
  const discountAmount = selectedPlan && coupon
    ? Math.max(0, Math.round((selectedPlan.amount_brl - discountedAmount) * 100) / 100)
    : 0;
  const noPaymentDue = Boolean(coupon && discountedAmount === 0);

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) {
      toast.error("Digite o código do cupom");
      return;
    }
    setApplyingCoupon(true);
    try {
      const { data } = await api.get(`/coupons/validate?code=${encodeURIComponent(code)}`);
      setCoupon(data);
      toast.success(`Cupom ${data.code} aplicado — ${data.discount_pct}% OFF`);
    } catch (error) {
      toast.error(formatApiError(error));
      setCoupon(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponInput("");
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!selectedPlan) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/vps/${vpsId}/upgrade`, {
        plan_id: selectedPlan.plan_id,
        payment_method: paymentMethod,
        coupon_code: coupon?.code || undefined,
      });
      toast.success(data.invoice?.status === "paid"
        ? "Cupom aplicado. O upgrade sem cobrança foi iniciado."
        : "Fatura de upgrade criada. O plano muda após a confirmação do pagamento.");
      onOpenChange(false);
      if (paymentMethod === "credit_card" && data.invoice?.mp_checkout_url) {
        window.location.assign(data.invoice.mp_checkout_url);
      } else if (data.invoice?.invoice_id) {
        navigate(`/dashboard/invoices/${data.invoice.invoice_id}`);
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm bg-[#121214] border-white/10 max-w-lg" data-testid="vps-upgrade-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tighter flex items-center gap-2">
            <ArrowUpRight className="h-5 w-5" /> Upgrade de plano
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-zinc-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Calculando o valor proporcional…
          </div>
        ) : options?.pending_upgrade ? (
          <div className="space-y-4 text-sm">
            <p className="text-zinc-300">
              Esta VPS já tem um upgrade em andamento.
              {options.pending_upgrade.application_state === "failed"
                ? " O pagamento foi confirmado, mas a aplicação precisa de revisão da equipe."
                : options.pending_upgrade.status === "paid"
                  ? " O pagamento foi confirmado e os recursos estão sendo aplicados."
                  : " Conclua o pagamento ou acompanhe a fatura existente."}
            </p>
            <Button asChild className="w-full rounded-sm bg-white text-black hover:bg-zinc-200">
              <Link to={`/dashboard/invoices/${options.pending_upgrade.invoice_id}`}>
                Abrir fatura
              </Link>
            </Button>
          </div>
        ) : options?.reason ? (
          <p className="text-sm text-zinc-400" data-testid="vps-upgrade-unavailable">
            {options.reason}
          </p>
        ) : selectedPlan ? (
          <form onSubmit={submit} className="space-y-5 text-sm">
            <div className="border border-white/10 bg-[#09090B] p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Plano atual</div>
              <div className="mt-1 flex items-center justify-between gap-3">
                <span className="font-semibold">{options.current_plan?.name}</span>
                <span className="font-mono">{fmtBRL(options.current_plan?.price_brl)}/mês</span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">
                Escolha o novo plano
              </label>
              <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                <SelectTrigger data-testid="vps-upgrade-plan-select" className="rounded-sm bg-[#09090B] border-white/10">
                  <SelectValue placeholder="Selecione um plano" />
                </SelectTrigger>
                <SelectContent className="rounded-sm bg-[#121214] border-white/10">
                  {options.plans.map((plan) => (
                    <SelectItem key={plan.plan_id} value={plan.plan_id}>
                      {plan.name} · {plan.cpu_cores} vCPU · {plan.ram_gb} GB RAM · {plan.ssd_gb} GB SSD
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3 border border-white/10 bg-[#09090B] p-4">
              <label htmlFor="vps-upgrade-coupon" className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500">
                <TicketIcon className="h-3.5 w-3.5" /> Cupom de desconto (opcional)
              </label>
              {!coupon ? (
                <div className="flex gap-2">
                  <Input
                    id="vps-upgrade-coupon"
                    value={couponInput}
                    onChange={(event) => setCouponInput(event.target.value.toUpperCase())}
                    onKeyDown={(event) => event.key === "Enter" && (event.preventDefault(), applyCoupon())}
                    placeholder="Código do cupom"
                    className="rounded-sm bg-[#121214] border-white/10 font-mono uppercase tracking-widest"
                    data-testid="vps-upgrade-coupon-input"
                  />
                  <Button
                    type="button"
                    onClick={applyCoupon}
                    disabled={applyingCoupon || !couponInput.trim()}
                    variant="outline"
                    className="rounded-sm border-white/20 hover:bg-white hover:text-black"
                    data-testid="vps-upgrade-coupon-apply"
                  >
                    {applyingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aplicar"}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3 border border-emerald-500/30 bg-emerald-500/10 px-3 py-2">
                  <div className="min-w-0">
                    <div className="font-mono text-sm text-emerald-300">
                      {coupon.code} — {coupon.discount_pct}% OFF
                    </div>
                    {coupon.description && (
                      <div className="text-xs text-emerald-200/70">{coupon.description}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={removeCoupon}
                    className="shrink-0 text-emerald-300 hover:text-white"
                    aria-label="Remover cupom"
                    data-testid="vps-upgrade-coupon-remove"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>

            <div className="border border-emerald-500/25 bg-emerald-500/5 p-4 space-y-2">
              <div className="flex justify-between gap-3">
                <span className="text-zinc-400">Novo plano</span>
                <span className="text-right font-semibold">{selectedPlan.name}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-zinc-400">Recursos</span>
                <span className="text-right font-mono text-xs">
                  {selectedPlan.cpu_cores} vCPU · {selectedPlan.ram_gb} GB RAM · {selectedPlan.ssd_gb} GB SSD
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-zinc-400">Diferença mensal</span>
                <span className="font-mono">{fmtBRL(selectedPlan.price_difference_brl)}</span>
              </div>
              {coupon && (
                <div className="flex justify-between gap-3 text-emerald-300" data-testid="vps-upgrade-coupon-discount">
                  <span>Cupom {coupon.code} · {coupon.discount_pct}% OFF</span>
                  <span className="font-mono">−{fmtBRL(discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between gap-3 border-t border-white/10 pt-2">
                <span className="text-zinc-300">Cobrança até a renovação</span>
                <span className="font-mono font-bold text-emerald-300" data-testid="vps-upgrade-total">
                  {fmtBRL(discountedAmount)}
                </span>
              </div>
            </div>

            <p className="text-xs leading-relaxed text-zinc-400">
              Cálculo proporcional para {selectedPlan.remaining_days} dias restantes do ciclo de 30 dias.
              A próxima renovação continua em {fmtDate(options.expires_at)}.
            </p>

            <div className="flex items-start gap-2 border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200/90">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>A VPS pode reiniciar por alguns minutos para aplicar CPU, memória e disco. Os dados não serão apagados.</span>
            </div>

            {noPaymentDue ? (
              <p className="border border-emerald-500/25 bg-emerald-500/5 p-3 text-xs text-emerald-200" data-testid="vps-upgrade-no-payment">
                O cupom cobre todo o valor proporcional. O upgrade será iniciado sem gerar cobrança.
              </p>
            ) : (
              <div className="space-y-2">
                <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">
                  Forma de pagamento
                </label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger data-testid="vps-upgrade-payment-method" className="rounded-sm bg-[#09090B] border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-sm bg-[#121214] border-white/10">
                    {(options.payment_methods || ["pix"]).map((method) => (
                      <SelectItem key={method} value={method}>
                        {method === "credit_card" ? "Cartão de crédito" : "PIX"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting || applyingCoupon || !selectedPlan}
              className="w-full rounded-sm bg-white text-black hover:bg-zinc-200"
              data-testid="vps-upgrade-submit"
            >
              {submitting
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Criando fatura…</>
                : noPaymentDue
                  ? "Confirmar upgrade sem cobrança"
                  : <>Continuar para pagamento · {fmtBRL(discountedAmount)}</>}
            </Button>
          </form>
        ) : (
          <div className="py-6 text-center text-sm text-zinc-400">Nenhum plano compatível disponível.</div>
        )}
      </DialogContent>
    </Dialog>
  );
}